import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import MobileNav from "../components/MobileNav";
import Footer1 from "../components/Footer1";
import "./OriginalDetails.css";
import paintingsData from "../data/paintings.json";
// Compatible with the existing SEOProvider in the V4 SEO package.
import { usePageSEO } from "../components/SEOManager";

const OriginalDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [painting, setPainting] = useState(null);
    const [relatedWorks, setRelatedWorks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeImageIndex, setActiveImageIndex] = useState(0);
    const [isLightboxOpen, setIsLightboxOpen] = useState(false);

    // Read More State
    const [isExpanded, setIsExpanded] = useState(false);

    // Audio Narrative States (Static HTML5 Audio)
    const [audioPlayer, setAudioPlayer] = useState(null);
    const [isSpeaking, setIsSpeaking] = useState(false);
    const [currentLang, setCurrentLang] = useState(null);

    // Clean up audio if the user leaves the page or changes the painting
    useEffect(() => {
        return () => {
            if (audioPlayer) {
                audioPlayer.pause();
                audioPlayer.src = "";
            }
        };
    }, [audioPlayer]);

    useEffect(() => {
        window.scrollTo(0, 0);

        // 1. Lock screen scroll & set loading to true
        setLoading(true);
        document.body.style.overflow = "hidden";

        // Stop any currently playing audio when switching pages
        if (audioPlayer) {
            audioPlayer.pause();
            audioPlayer.src = "";
        }
        setIsSpeaking(false);
        setCurrentLang(null);
        setAudioPlayer(null);

        // 2. Fetch the data immediately so the DOM can build in the background
        const foundPainting = paintingsData.find((p) => p.id === parseInt(id));
        setPainting(foundPainting || null);

        if (foundPainting) {
            const related = paintingsData
                .filter(p => p.category === foundPainting.category && p.id !== foundPainting.id && (!p.series || p.series.trim() === ""))
                .slice(0, 3);
            setRelatedWorks(related);
        } else {
            setRelatedWorks([]);
        }

        // Reset states when changing paintings
        setIsExpanded(false);

        // 3. Keep the aesthetic loader on screen for 1.8 seconds while images fetch in background
        const timer = setTimeout(() => {
            setLoading(false);
            document.body.style.overflow = "auto"; // Unlock scroll
        }, 1800);

        return () => {
            clearTimeout(timer);
            document.body.style.overflow = "auto";
        };
    }, [id]);

    const fadeUp = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 1.2, ease: [0.16, 1, 0.3, 1], delay: 0.3 } }
    };

    // Guard against accidentally showing the previous artwork's SEO when the URL ID changes.
    const seoPainting = painting && String(painting.id) === String(id) ? painting : null;

    const images = painting?.images || [];
    const mainImageUrl = images[activeImageIndex]?.imageUrl || "https://via.placeholder.com/600x800";
    const interiorImage = images.length >= 3 ? images[2].imageUrl : null;

    // Text Truncation & HTML Handling Logic
    const rawStory = painting?.story || "A profound narrative captured through unparalleled texture and form.";
    const stripHtml = (html) => html.replace(/<[^>]*>?/gm, ' ');
    const plainTextStory = stripHtml(rawStory);

    // Stable, painting-specific metadata for the browser tab and Google.
    // Always use the BASE image for social previews, even when a user switches
    // gallery thumbnails.
    const primaryArtworkImage =
        seoPainting?.images?.find((image) => image.imageType === "BASE")?.imageUrl ||
        seoPainting?.images?.[0]?.imageUrl;
    const cleanStory = plainTextStory
        .replace(/&nbsp;|&#160;/gi, " ")
        .replace(/&amp;/gi, "&")
        .replace(/&quot;/gi, '\"')
        .replace(/&#39;|&apos;/gi, "'")
        .replace(/\s+/g, " ")
        .trim();
    const paintingTitle = seoPainting ? `${seoPainting.title} | Anjaan Vivek` : "Original Artwork | Anjaan Vivek";
    const paintingDescription = seoPainting
        ? `${seoPainting.title}, an original painting by contemporary artist Anjaan Vivek. ${cleanStory}`.slice(0, 160).trim()
        : "Discover original paintings and their stories by artist Anjaan Vivek.";
    const canonicalPath = `/originalDetails/${encodeURIComponent(id)}`;
    const canonicalUrl = `https://anjaanvivek.com${canonicalPath}`;

    // Uses the SEOProvider from your updated App.js. This is deliberately the
    // only owner of <title>, canonical, Open Graph and JSON-LD for this route.
    // Do not add a second react-helmet-async <Helmet> for the same values.
    usePageSEO(seoPainting ? {
        title: paintingTitle,
        description: paintingDescription,
        image: primaryArtworkImage,
        imageAlt: `${seoPainting.title}, original painting by Anjaan Vivek`,
        canonicalPath,
        type: "article",
        noindex: false,
        structuredData: {
            "@context": "https://schema.org",
            "@type": "VisualArtwork",
            name: seoPainting.title,
            url: canonicalUrl,
            ...(primaryArtworkImage ? { image: primaryArtworkImage } : {}),
            creator: {
                "@type": "Person",
                name: "Anjaan Vivek",
                url: "https://anjaanvivek.com/about"
            },
            artform: "Painting",
            ...(seoPainting.material ? { artMedium: seoPainting.material } : {}),
            ...(seoPainting.year ? { dateCreated: String(seoPainting.year) } : {}),
            description: cleanStory.slice(0, 1000)
            // No Offer is declared because pricing is by private enquiry.
        }
    } : !loading && !painting ? {
        title: "Masterwork Not Found | Anjaan Vivek",
        description: "This artwork could not be found.",
        noindex: true,
        canonicalPath
    } : null);

    if (!painting && !loading) return (
        <div className="elite-empty-state" style={{ paddingTop: "20vh" }}>Masterwork not found.</div>
    );

    const maxLength = 250;
    const isLongText = plainTextStory.length > maxLength;

    // --- STATIC AUDIO NARRATIVE FUNCTIONS ---
    const handleStop = () => {
        if (audioPlayer) {
            audioPlayer.pause();
            audioPlayer.currentTime = 0;
        }
        setIsSpeaking(false);
        setCurrentLang(null);
    };

    const handleSpeak = (langCode) => {
        handleStop(); // Stop any currently playing audio

        // Select the correct MP3 URL from your JSON data
        let audioUrl = langCode === 'hi-IN' ? painting?.audioHi : painting?.audioEn;

        if (!audioUrl) {
            alert(`The ${langCode === 'hi-IN' ? 'Hindi' : 'English'} audio narration is not yet available for this masterwork.`);
            return;
        }

        const newAudio = new Audio(audioUrl);
        setAudioPlayer(newAudio);
        setIsSpeaking(true);
        setCurrentLang(langCode);

        newAudio.onended = () => {
            setIsSpeaking(false);
            setCurrentLang(null);
        };

        newAudio.play().catch(err => {
            console.error("Audio playback failed:", err);
            setIsSpeaking(false);
            setCurrentLang(null);
            alert("Failed to play the audio. Please check your connection.");
        });
    };

    // Inquiry Messages
    const whatsappMessage = `Dear Studio,\n\nI am writing to formally request the private pricing and acquisition dossier for the original masterpiece '${painting?.title}'.\n\nPlease let me know the next steps.`;
    const emailSubject = encodeURIComponent(`Private Pricing Request: ${painting?.title}`);
    const emailBody = encodeURIComponent(`Dear Anjaan Vivek Studio,\n\nI would like to formally request the private pricing and acquisition dossier for the original masterpiece '${painting?.title}'.\n\nPlease let me know the next steps for collector approval.\n\nSincerely,`);

    return (
        <div className="original-details-page">
            <AnimatePresence>
                {loading && (
                    <motion.div
                        key="elite-loader"
                        initial={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.8, ease: "easeInOut" }}
                        style={{
                            position: "fixed",
                            inset: 0,
                            zIndex: 99999,
                            backgroundColor: "#ffffff",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center"
                        }}
                    >
                        <motion.span
                            animate={{ opacity: [0.3, 1, 0.3] }}
                            transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
                            style={{
                                fontFamily: "'Cormorant Garamond', serif",
                                fontSize: "1rem",
                                textTransform: "uppercase",
                                letterSpacing: "6px",
                                color: "#111111",
                                marginBottom: "20px"
                            }}
                        >
                            Curating Masterwork
                        </motion.span>
                        <div style={{ width: "80px", height: "1px", backgroundColor: "rgba(0,0,0,0.05)", overflow: "hidden", position: "relative" }}>
                            <motion.div
                                initial={{ x: "-100%" }}
                                animate={{ x: "100%" }}
                                transition={{ repeat: Infinity, duration: 1.2, ease: "easeInOut" }}
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    backgroundColor: "#c4a36e",
                                    position: "absolute"
                                }}
                            />
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <MobileNav />

            {painting && (
                <>
                    {interiorImage ? (
                        <section className="original-interior-hero">
                            <motion.div
                                className="interior-hero-bg"
                                style={{ backgroundImage: `url(${interiorImage})` }}
                                initial={{ scale: 1.05 }}
                                animate={{ scale: !loading ? 1 : 1.05 }}
                                transition={{ duration: 1.5, ease: "easeOut", delay: 0.3 }}
                            ></motion.div>
                            <div className="interior-hero-overlay"></div>
                            <motion.div className="interior-hero-content" initial="hidden" animate={!loading ? "visible" : "hidden"} variants={fadeUp}>
                                <span className="interior-eyebrow">Viewing Room</span>
                                <h2>{painting.title}</h2>
                            </motion.div>
                        </section>
                    ) : (
                        <div className="nav-spacer"></div>
                    )}

                    <div className="original-dossier-container">
                        {/* LEFT: Masterwork Visuals */}
                        <motion.div className="original-visuals" initial="hidden" animate={!loading ? "visible" : "hidden"} variants={fadeUp}>
                            <div className="original-main-image-wrapper" onClick={() => setIsLightboxOpen(true)}>
                                <img src={mainImageUrl} alt={painting.title} className="original-main-image" />
                                <div className="expand-hint"><i className="fa-solid fa-expand"></i> Inspect</div>
                            </div>

                            {images.length > 1 && (
                                <div className="original-thumbnails">
                                    {images.map((img, index) => (
                                        index < 2 && <img
                                            key={img.id || index} src={img.imageUrl} alt={`Detail ${index + 1}`}
                                            className={activeImageIndex === index ? "active-thumb" : ""}
                                            onClick={() => setActiveImageIndex(index)}
                                        />
                                    ))}
                                </div>
                            )}
                        </motion.div>

                        {/* RIGHT: Curatorial Details & Inquiry */}
                        <motion.div className="original-data" initial="hidden" animate={!loading ? "visible" : "hidden"} variants={fadeUp}>
                            <span className="curatorial-tagD tag-singularD">Original Masterwork</span>
                            <h1 className="original-title">{painting.title}</h1>
                            <p className="original-artist">By Anjaan Vivek</p>

                            <div className="original-specs-grid">
                                <div className="spec-item">
                                    <span className="spec-label">Medium</span>
                                    <span className="spec-value">{painting.material || "Mixed Media"}</span>
                                </div>
                                <div className="spec-item">
                                    <span className="spec-label">Dimensions</span>
                                    <span className="spec-value">{painting.size || "Available on request"}</span>
                                </div>
                                <div className="spec-item">
                                    <span className="spec-label">Creation Year</span>
                                    <span className="spec-value">{painting.year || "Not specified"}</span>
                                </div>
                                <div className="spec-item">
                                    <span className="spec-label">Availability</span>
                                    <span className="spec-value availability-status">{painting.sold ? "Acquired" : "Available"}</span>
                                </div>
                            </div>

                            {/* AUDIO CONTROLS */}
                            <div className="audio-controls" style={{ marginBottom: "20px", display: "flex", gap: "10px", alignItems: "center" }}>
                                <span style={{ fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "2px", color: "#999", marginRight: "10px" }}>
                                    Listen:
                                </span>

                                {
                                    painting?.audioEn?.length > 0 &&

                                    <button
                                        onClick={() => currentLang === 'en-US' && isSpeaking ? handleStop() : handleSpeak('en-US')}
                                        className="btn-audio"
                                        style={{
                                            background: "transparent", border: "1px solid #c4a36e", color: currentLang === 'en-US' && isSpeaking ? "#c4a36e" : "#111",
                                            padding: "5px 15px", fontSize: "0.8rem", cursor: "pointer", transition: "0.3s"
                                        }}
                                    >
                                        <i className={`fa-solid ${currentLang === 'en-US' && isSpeaking ? 'fa-stop' : 'fa-play'}`}></i> English
                                    </button>
                                }
                                {
                                    painting?.audioHi?.length > 0 &&
                                    <button
                                        onClick={() => currentLang === 'hi-IN' && isSpeaking ? handleStop() : handleSpeak('hi-IN')}
                                        className="btn-audio"
                                        style={{
                                            background: "transparent", border: "1px solid #c4a36e", color: currentLang === 'hi-IN' && isSpeaking ? "#c4a36e" : "#111",
                                            padding: "5px 15px", fontSize: "0.8rem", cursor: "pointer", transition: "0.3s"
                                        }}
                                    >
                                        <i className={`fa-solid ${currentLang === 'hi-IN' && isSpeaking ? 'fa-stop' : 'fa-play'}`}></i> हिंदी
                                    </button>
                                }
                            </div>
                            {/* STORY SECTION WITH HTML AND READ MORE */}
                            <div className="original-story">
                                {isExpanded || !isLongText ? (
                                    <div dangerouslySetInnerHTML={{ __html: rawStory }} />
                                ) : (
                                    <p style={{ whiteSpace: "pre-line", marginBottom: 0 }}>
                                        {plainTextStory.substring(0, maxLength)}...
                                    </p>
                                )}

                                {isLongText && (
                                    <span
                                        onClick={() => setIsExpanded(!isExpanded)}
                                        style={{
                                            color: "var(--accent-champagne)",
                                            cursor: "pointer",
                                            fontWeight: "500",
                                            fontSize: "0.9rem",
                                            display: "inline-block",
                                            marginTop: "12px"
                                        }}
                                    >
                                        {isExpanded ? "Read Less" : "Read More"}
                                    </span>
                                )}
                            </div>

                            {!painting.sold && (
                                <div className="original-inquiry-box">
                                    <h3 className="inquiry-title">Acquisition</h3>
                                    <p className="inquiry-desc">Pricing is disclosed exclusively upon private request. Please contact the studio concierge to initiate the acquisition process.</p>

                                    <div className="inquiry-actions">
                                        <a href={`https://wa.me/919300847841?text=${encodeURIComponent(whatsappMessage)}`} target="_blank" rel="noreferrer" className="btn-elite-whatsapp">
                                            <i className="fa-brands fa-whatsapp"></i> Direct Inquiry
                                        </a>
                                        <a href={`mailto:anjaanvivek17@gmail.com?subject=${emailSubject}&body=${emailBody}`} className="btn-elite-email">
                                            <i className="fa-regular fa-envelope"></i> Request Dossier
                                        </a>
                                    </div>
                                </div>
                            )}

                            <div className="trust-indicators">
                                <div className="trust-item"><i className="fa-solid fa-certificate"></i><span>Authenticity Guaranteed</span></div>
                                <div className="trust-item"><i className="fa-solid fa-plane-shield"></i><span>Global White-Glove Delivery</span></div>
                            </div>
                        </motion.div>
                    </div>

                    {/* RELATED WORKS */}
                    {relatedWorks.length > 0 && (
                        <div className="related-originals-section">
                            <motion.div className="related-header" initial="hidden" animate={!loading ? "visible" : "hidden"} variants={fadeUp}>
                                <span className="curatorial-tag-gold" style={{ color: "#c4a36e" }}>Curated Recommendations</span>
                                <h2>More Singular Masterworks</h2>
                            </motion.div>
                            <motion.div className="related-grid" initial="hidden" animate={!loading ? "visible" : "hidden"} variants={fadeUp}>
                                {relatedWorks.map(related => (
                                    <div key={related.id} className="related-card" onClick={() => navigate(`/originalDetails/${related.id}`)}>
                                        <div className="related-img-wrapper">
                                            <img src={related.images?.[0]?.imageUrl || "https://via.placeholder.com/600x800"} alt={related.title} />
                                        </div>
                                        <h4>{related.title}</h4>
                                    </div>
                                ))}
                            </motion.div>
                        </div>
                    )}
                </>
            )}

            <AnimatePresence>
                {isLightboxOpen && (
                    <div className="elite-lightbox-overlay" onClick={() => setIsLightboxOpen(false)}>
                        <motion.div className="elite-lightbox-content" onClick={(e) => e.stopPropagation()} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}>
                            <button className="elite-lightbox-close" onClick={() => setIsLightboxOpen(false)}>&times;</button>
                            <img src={mainImageUrl} alt={painting?.title} className="elite-lightbox-img" />
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            <Footer1 />
        </div>
    );
};

export default OriginalDetails;